import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Window, Dialog, ConfirmDialog, EmptyState, Loading } from "../../components/VB";
import RupiahInput from "../../components/RupiahInput";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Wallet, AlertTriangle, CheckCircle2, Clock } from "lucide-react";

const fmt = (n) => "Rp " + (n || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });

const StatusBadge = ({ status }) => {
  const cls = status === "Lunas" ? "vb-badge-approved" : status === "Jatuh Tempo" ? "vb-badge-rejected" : "vb-badge-pending";
  const Icon = status === "Lunas" ? CheckCircle2 : status === "Jatuh Tempo" ? AlertTriangle : Clock;
  return (
    <span className={`vb-badge ${cls}`}>
      <Icon className="w-3 h-3 inline mr-1" />
      {status}
    </span>
  );
};

const emptyForm = {
  nomor_piutang: "", nama_pelanggan: "", deskripsi: "", tanggal_piutang: "",
  tanggal_jatuh_tempo: "", nominal_total: 0, catatan: ""
};

export default function Hutang() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [del, setDel] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ tanggal: "", nominal: 0, metode: "Transfer", catatan: "" });
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");

  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const [p, s] = await Promise.all([
        api.get("/hutang", { params }),
        api.get("/hutang/summary"),
      ]);
      setItems(p.data);
      setSummary(s.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus]);

  const openCreate = () => {
    setForm({ ...emptyForm, tanggal_piutang: today, tanggal_jatuh_tempo: today });
    setEditId(null); setOpen(true);
  };
  const openEdit = (it) => { setForm({ ...emptyForm, ...it }); setEditId(it.id); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, nominal_total: parseFloat(form.nominal_total) };
      if (editId) await api.put(`/hutang/${editId}`, payload);
      else await api.post("/hutang", payload);
      toast.success("Hutang tersimpan");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/hutang/${del.id}`); toast.success("Dihapus"); setDel(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openPay = (p) => {
    setPayTarget(p);
    setPayForm({ tanggal: today, nominal: p.nominal_sisa, metode: "Transfer", catatan: "" });
  };

  const submitPay = async () => {
    try {
      await api.post(`/hutang/${payTarget.id}/payment`, { ...payForm, nominal: parseFloat(payForm.nominal) });
      toast.success("Pembayaran hutang dicatat & masuk arus kas keluar");
      setPayTarget(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openHistory = async (p) => {
    setHistoryTarget(p);
    try {
      const { data } = await api.get(`/hutang/${p.id}/payments`);
      setHistoryItems(data);
    } catch {}
  };

  const deletePayment = async (payId) => {
    if (!window.confirm("Hapus pembayaran ini? Akan rollback ke hutang & arus kas.")) return;
    try {
      await api.delete(`/hutang/${historyTarget.id}/payment/${payId}`);
      toast.success("Pembayaran dihapus");
      const { data } = await api.get(`/hutang/${historyTarget.id}/payments`);
      setHistoryItems(data);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Window
      title="Hutang ke Vendor / Supplier"
      actions={<button className="vb-btn" onClick={openCreate} data-testid="add-hutang"><Plus className="w-3 h-3 inline mr-1" />Tambah Hutang</button>}
    >
      {summary && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8, marginBottom: 12 }}>
          <div className="vb-statcard">
            <div className="vb-statcard-label">Total Hutang</div>
            <div className="vb-statcard-value" style={{ color: "#003399", fontSize: 18 }}>{fmt(summary.total_hutang)}</div>
          </div>
          <div className="vb-statcard">
            <div className="vb-statcard-label">Sudah Dibayar</div>
            <div className="vb-statcard-value" style={{ color: "#4f8a10", fontSize: 18 }}>{fmt(summary.total_dibayar)}</div>
          </div>
          <div className="vb-statcard">
            <div className="vb-statcard-label">Sisa Hutang</div>
            <div className="vb-statcard-value" style={{ color: "#d8000c", fontSize: 18 }}>{fmt(summary.total_sisa_hutang)}</div>
          </div>
          <div className="vb-statcard"><div className="vb-statcard-label">Lunas</div><div className="vb-statcard-value" style={{ color: "#4f8a10" }}>{summary.count_lunas}</div></div>
          <div className="vb-statcard"><div className="vb-statcard-label">Belum Lunas</div><div className="vb-statcard-value" style={{ color: "#9f6000" }}>{summary.count_belum_lunas}</div></div>
          <div className="vb-statcard"><div className="vb-statcard-label">Jatuh Tempo</div><div className="vb-statcard-value" style={{ color: "#d8000c" }}>{summary.count_jatuh_tempo}</div></div>
        </div>
      )}

      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <select className="vb-select" style={{ width: 180 }} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="hutang-filter-status">
          <option value="">Semua Status</option>
          <option>Belum Lunas</option>
          <option>Lunas</option>
          <option>Jatuh Tempo</option>
        </select>
      </div>

      {loading ? <Loading /> : items.length === 0 ? <EmptyState message="Belum ada data hutang" /> : (
        <div style={{ overflowX: "auto" }}>
          <table className="vb-table">
            <thead>
              <tr>
                <th>No. Hutang</th><th>Vendor</th><th>Deskripsi</th>
                <th>Tgl Hutang</th><th>Jatuh Tempo</th>
                <th style={{ textAlign: "right" }}>Total</th>
                <th style={{ textAlign: "right" }}>Dibayar</th>
                <th style={{ textAlign: "right" }}>Sisa</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td style={{ fontFamily: "Consolas, monospace", fontSize: 11 }}>{it.nomor_piutang}</td>
                  <td><strong>{it.nama_pelanggan}</strong></td>
                  <td>{it.deskripsi}</td>
                  <td>{it.tanggal_piutang}</td>
                  <td>{it.tanggal_jatuh_tempo}</td>
                  <td style={{ textAlign: "right" }}>{fmt(it.nominal_total)}</td>
                  <td style={{ textAlign: "right", color: "#4f8a10" }}>{fmt(it.nominal_dibayar)}</td>
                  <td style={{ textAlign: "right", color: "#d8000c", fontWeight: 600 }}>{fmt(it.nominal_sisa)}</td>
                  <td><StatusBadge status={it.status} /></td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {it.status !== "Lunas" && (
                      <button className="vb-btn-primary" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => openPay(it)} data-testid={`hutang-pay-${it.id}`}>
                        <Wallet className="w-3 h-3 inline mr-1" />Bayar
                      </button>
                    )}
                    <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => openHistory(it)} data-testid={`hutang-history-${it.id}`}>
                      Riwayat
                    </button>
                    {it.nominal_dibayar === 0 && (
                      <>
                        <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => openEdit(it)}>
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(it)}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={open} onClose={() => setOpen(false)}
        title={editId ? "Edit Hutang" : "Tambah Hutang Baru"} width={560}
        footer={<>
          <button className="vb-btn" onClick={() => setOpen(false)}>Batal</button>
          <button className="vb-btn-primary" onClick={submit} data-testid="hutang-save">Simpan</button>
        </>}
      >
        <form onSubmit={submit}>
          <div className="vb-form-row">
            <label className="vb-label">No. Hutang (opsional - auto)</label>
            <input className="vb-input" value={form.nomor_piutang} onChange={(e) => setForm({ ...form, nomor_piutang: e.target.value })} placeholder="HUT-20260617-001" />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Nama Vendor / Supplier*</label>
            <input className="vb-input" value={form.nama_pelanggan} onChange={(e) => setForm({ ...form, nama_pelanggan: e.target.value })} required data-testid="hutang-vendor" />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Deskripsi*</label>
            <textarea rows={2} className="vb-textarea" value={form.deskripsi} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} required placeholder="Pembelian alat tulis kantor, sewa server, dll" data-testid="hutang-deskripsi" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div className="vb-form-row">
              <label className="vb-label">Tanggal Hutang*</label>
              <input type="date" className="vb-input" value={form.tanggal_piutang} onChange={(e) => setForm({ ...form, tanggal_piutang: e.target.value })} required data-testid="hutang-tanggal" />
            </div>
            <div className="vb-form-row">
              <label className="vb-label">Jatuh Tempo*</label>
              <input type="date" className="vb-input" value={form.tanggal_jatuh_tempo} onChange={(e) => setForm({ ...form, tanggal_jatuh_tempo: e.target.value })} required data-testid="hutang-jt" />
            </div>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Nominal Total*</label>
            <RupiahInput value={form.nominal_total} onChange={(v) => setForm({ ...form, nominal_total: v })} required testId="hutang-nominal" />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Catatan</label>
            <textarea rows={2} className="vb-textarea" value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} />
          </div>
        </form>
      </Dialog>

      <Dialog
        open={!!payTarget} onClose={() => setPayTarget(null)} title="Bayar Hutang" width={480}
        footer={<>
          <button className="vb-btn" onClick={() => setPayTarget(null)}>Batal</button>
          <button className="vb-btn-primary" onClick={submitPay} data-testid="hutang-pay-save">
            <Wallet className="w-3 h-3 inline mr-1" />Bayarkan
          </button>
        </>}
      >
        {payTarget && (
          <>
            <div style={{ background: "#FFFFE1", border: "1px solid #828790", padding: 8, fontSize: 12, marginBottom: 12 }}>
              Vendor: <strong>{payTarget.nama_pelanggan}</strong> &middot; {payTarget.nomor_piutang}<br />
              Total: {fmt(payTarget.nominal_total)} &middot; Dibayar: {fmt(payTarget.nominal_dibayar)}<br />
              <strong style={{ color: "#d8000c" }}>Sisa: {fmt(payTarget.nominal_sisa)}</strong>
            </div>
            <div className="vb-form-row">
              <label className="vb-label">Tanggal Bayar*</label>
              <input type="date" className="vb-input" value={payForm.tanggal} onChange={(e) => setPayForm({ ...payForm, tanggal: e.target.value })} data-testid="hutang-pay-tanggal" />
            </div>
            <div className="vb-form-row">
              <label className="vb-label">Nominal Dibayar* (Maks {fmt(payTarget.nominal_sisa)})</label>
              <RupiahInput value={payForm.nominal} onChange={(v) => setPayForm({ ...payForm, nominal: v })} testId="hutang-pay-nominal" />
            </div>
            <div className="vb-form-row">
              <label className="vb-label">Metode*</label>
              <select className="vb-select" value={payForm.metode} onChange={(e) => setPayForm({ ...payForm, metode: e.target.value })}>
                <option value="Transfer">Transfer Bank</option>
                <option value="Cash">Cash / Tunai</option>
              </select>
            </div>
            <div className="vb-form-row">
              <label className="vb-label">Catatan</label>
              <textarea rows={2} className="vb-textarea" value={payForm.catatan} onChange={(e) => setPayForm({ ...payForm, catatan: e.target.value })} />
            </div>
            <div style={{ fontSize: 11, color: "#003399", padding: 6, background: "#e8f4ff", border: "1px solid #c4d9f1", marginTop: 8 }}>
              ℹ Pembayaran akan otomatis dicatat di Arus Kas Keluar (kategori "Pembayaran Hutang")
            </div>
          </>
        )}
      </Dialog>

      <Dialog
        open={!!historyTarget} onClose={() => { setHistoryTarget(null); setHistoryItems([]); }}
        title={`Riwayat Pembayaran - ${historyTarget?.nomor_piutang || ""}`} width={620}
        footer={<button className="vb-btn" onClick={() => { setHistoryTarget(null); setHistoryItems([]); }}>Tutup</button>}
      >
        {historyTarget && (
          <>
            <div style={{ marginBottom: 10, fontSize: 12 }}>
              <strong>Vendor: {historyTarget.nama_pelanggan}</strong> - {historyTarget.deskripsi}<br />
              Total: <strong>{fmt(historyTarget.nominal_total)}</strong> &middot;{" "}
              Dibayar: <strong style={{ color: "#4f8a10" }}>{fmt(historyTarget.nominal_dibayar)}</strong> &middot;{" "}
              Sisa: <strong style={{ color: "#d8000c" }}>{fmt(historyTarget.nominal_sisa)}</strong>
            </div>
            {historyItems.length === 0 ? <EmptyState message="Belum ada pembayaran" /> : (
              <table className="vb-table">
                <thead><tr><th>Tanggal</th><th>Nominal</th><th>Metode</th><th>Catatan</th><th>Aksi</th></tr></thead>
                <tbody>
                  {historyItems.map((p) => (
                    <tr key={p.id}>
                      <td>{p.tanggal}</td>
                      <td style={{ color: "#d8000c", fontWeight: 600 }}>{fmt(p.nominal)}</td>
                      <td>{p.metode}</td>
                      <td>{p.catatan || "-"}</td>
                      <td><button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => deletePayment(p.id)}><Trash2 className="w-3 h-3" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </Dialog>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message={`Hapus hutang ${del?.nomor_piutang}?`} />
    </Window>
  );
}
